import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Grid,
    Box,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorIcon from "@material-ui/icons/Error";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimelineIcon from "@material-ui/icons/Timeline";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import LinkIcon from "@material-ui/icons/Link";
import RefreshIcon from "@material-ui/icons/Refresh";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: 24,
        backgroundColor: "#f8f9fa",
    },
    header: {
        marginBottom: 24,
    },
    title: {
        fontSize: 24,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        color: "#666",
    },
    searchPaper: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    searchRow: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
    searchField: {
        width: 360,
        "& .MuiOutlinedInput-root": {
            borderRadius: 6,
        },
    },
    searchButton: {
        height: 40,
        borderRadius: 6,
        textTransform: "none",
        fontWeight: 500,
        paddingLeft: 20,
        paddingRight: 20,
    },
    userCard: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    userHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 16,
    },
    userBasicInfo: {
        display: "flex",
        flexDirection: "column",
    },
    userName: {
        fontSize: 18,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 2,
    },
    userEmail: {
        fontSize: 14,
        color: "#666",
        marginBottom: 2,
    },
    userId: {
        fontSize: 12,
        color: "#999",
    },
    referralCode: {
        display: "inline-flex",
        alignItems: "center",
        padding: "8px 16px",
        backgroundColor: "#e8f5e9",
        borderRadius: 6,
        border: "1px dashed #4caf50",
    },
    referralCodeLabel: {
        fontSize: 11,
        color: "#666",
        marginRight: 8,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    referralCodeValue: {
        fontSize: 16,
        fontWeight: 700,
        color: "#2e7d32",
        letterSpacing: 1,
    },
    statsGrid: {
        marginTop: 16,
    },
    statBox: {
        padding: 16,
        borderRadius: 6,
        backgroundColor: "#f8f9fa",
        textAlign: "center" as const,
    },
    statValue: {
        fontSize: 24,
        fontWeight: 700,
        color: "#1a1a1a",
        lineHeight: 1,
        marginBottom: 4,
    },
    statLabel: {
        fontSize: 11,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    statSuccess: { backgroundColor: "#e8f5e9", "& $statValue": { color: "#2e7d32" } },
    statPending: { backgroundColor: "#fff3e0", "& $statValue": { color: "#e65100" } },
    statFlagged: { backgroundColor: "#ffebee", "& $statValue": { color: "#c62828" } },
    statMoney: { backgroundColor: "#e3f2fd", "& $statValue": { color: "#1565c0" } },
    badgeRow: {
        display: "flex",
        gap: 8,
        marginTop: 12,
    },
    badge: {
        fontSize: 11,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: 4,
    },
    tablePaper: {
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    tableHeader: {
        padding: "16px 20px",
        borderBottom: "1px solid #eee",
    },
    tableTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    table: {
        "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: 12,
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            backgroundColor: "#f8f9fa",
            padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
            fontSize: 13,
            padding: "12px 16px",
        },
    },
    clickableRow: {
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#f8f9fa",
        },
    },
    statusChip: {
        fontSize: 11,
        fontWeight: 600,
        height: 24,
    },
    statusPending: { backgroundColor: "#fff3e0", color: "#e65100" },
    statusQualified: { backgroundColor: "#e3f2fd", color: "#1565c0" },
    statusCompleted: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
    statusRejected: { backgroundColor: "#ffebee", color: "#c62828" },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        padding: 40,
    },
    noData: {
        textAlign: "center" as const,
        padding: 40,
        color: "#999",
    },
    dialogContent: {
        minWidth: 600,
        maxHeight: "70vh",
        padding: 20,
    },
    commissionCard: {
        padding: 16,
        backgroundColor: "#f8f9fa",
        borderRadius: 6,
        marginBottom: 16,
    },
    commissionTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 12,
    },
    commissionGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
        gap: 16,
    },
    commissionItem: {
        textAlign: "center" as const,
    },
    commissionItemLabel: {
        fontSize: 10,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    commissionItemValue: {
        fontSize: 14,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    auditStep: {
        "& .MuiStepLabel-label": {
            fontWeight: 500,
        },
    },
    auditStepContent: {
        paddingLeft: 16,
    },
    auditTime: {
        fontSize: 11,
        color: "#999",
        marginBottom: 4,
    },
    auditData: {
        fontSize: 11,
        backgroundColor: "#f5f5f5",
        padding: 8,
        borderRadius: 4,
        maxHeight: 120,
        overflow: "auto",
        fontFamily: "monospace",
    },
    referredByCard: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        borderLeft: "4px solid #9c27b0",
    },
    referredByTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#9c27b0",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    referredByGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 16,
    },
    referredByItem: {
        padding: 12,
        backgroundColor: "#faf5fc",
        borderRadius: 6,
    },
    referredByLabel: {
        fontSize: 10,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    referredByValue: {
        fontSize: 14,
        fontWeight: 500,
        color: "#1a1a1a",
    },
    promocodeCard: {
        padding: 20,
        marginBottom: 16,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        borderLeft: "4px solid #ff9800",
    },
    promocodeTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#e65100",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    promocodeGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 16,
    },
    promocodeItem: {
        padding: 12,
        backgroundColor: "#fff8e1",
        borderRadius: 6,
    },
    promocodeLabel: {
        fontSize: 10,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    promocodeValue: {
        fontSize: 14,
        fontWeight: 500,
        color: "#1a1a1a",
    },
    promocodeCode: {
        fontFamily: "monospace",
        letterSpacing: 1,
        fontSize: 16,
        fontWeight: 700,
        color: "#e65100",
    },
    notReferredCard: {
        padding: 16,
        marginBottom: 16,
        borderRadius: 8,
        backgroundColor: "#f5f5f5",
        textAlign: "center" as const,
    },
    rewardsSection: {
        marginTop: 16,
        paddingTop: 16,
        borderTop: "1px solid #eee",
    },
    rewardsTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: "#666",
        marginBottom: 8,
    },
    prizeDrawBadge: {
        backgroundColor: "#ff9800",
        color: "#fff",
        fontSize: 11,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
    },
}));

interface Referral {
    id: number;
    referrer_user_id: number;
    referred_user_id: number;
    referral_code: string | null;
    status: "pending" | "qualified" | "completed" | "rejected";
    qualified_at: string | null;
    completed_at: string | null;
    createdAt: string;
    referred_email?: string;
    referred_name?: string;
}

interface AuditLogEntry {
    id: number;
    referral_id: number;
    event_type: string;
    event_data: any;
    source: string;
    previous_state: string | null;
    new_state: string | null;
    error_message: string | null;
    createdAt: string;
}

interface Commission {
    id: number;
    referral_id: number;
    commission_type: string;
    amount: number;
    status: string;
    is_inflight: boolean;
    fraud_status: string;
    config_id: number | null;
    createdAt: string;
}

interface UserData {
    id: number;
    email: string;
    name: string;
    referral_code: string | null;
}

interface UserStats {
    total_referrals: number;
    successful_referrals: number;
    pending_referrals: number;
    flagged_referrals: number;
    paid_out: number;
    inflight_amount: number;
    // Prize draw stats
    prize_draw_wins: number;
    prize_draw_paid_out: number;
    prize_draw_inflight: number;
    prize_draw_boost_weeks: number;
    // Ticket stats
    total_tickets: number;
    current_week_tickets: number;
    total_effective_tickets: number;
    is_ambassador: boolean;
    is_boost_active: boolean;
    boost_weeks_remaining: number;
    // Legacy referrals count
    legacy_referrals_count: number;
}

interface LegacyReferral {
    id: number;
    referred_user_id: number;
    referred_email: string | null;
    referred_name: string | null;
    created_at: string;
}

interface ReferredBy {
    referral_id: number;
    referrer_user_id: number;
    referrer_name: string;
    referrer_email: string;
    referral_code_used: string;
    status: string;
    qualified_at: string | null;
    completed_at: string | null;
    created_at: string;
    prize_draw_entry_id: number | null;
    prize_draw_ticket_number: number | null;
}

interface PromocodeUsed {
    code: string;
    amount: number;
    status: string;
    affiliate_name: string | null;
    is_remortgage_introducer: boolean;
    is_sws_introducer: boolean;
    used_at: string;
}

interface ReferredUserReward {
    id: number;
    commission_type: string;
    amount: number;
    status: string;
    is_inflight: boolean;
    fraud_status: string;
    boost_weeks_earned: number;
    createdAt: string;
}

interface PrizeDrawTicket {
    id: number;
    prize_draw_week_id: number;
    ticket_number: number;
    multiplier_applied: number;
    effective_tickets: number;
    entry_status: string;
    entry_timestamp: string;
    referral_id: number;
    week_start: string;
    week_end: string;
    week_status: string;
    campaign_name: string;
    referred_email: string;
    referred_name: string;
}

interface CommissionPreview {
    campaignName: string;
    campaignId: number;
    referrer: {
        userId: number;
        email: string;
        name: string;
        rewards: {
            isPrizeDrawMode: boolean;
            isAmbassador: boolean;
            wouldBecomeAmbassador: boolean;
            currentReferrals: number;
            ambassadorThreshold: number;
            prizeDrawTicket?: {
                weekId: number;
                multiplier: number;
                basePrize: number;
                effectiveTickets: number;
            };
            commission?: {
                amount: number;
                commissionType: string;
                boostWeeks: number;
                boostEnabled: boolean;
                sprivePlus: boolean;
            };
        };
    };
    referred: {
        userId: number;
        email: string;
        name: string;
        rewards: {
            bonusAmount: number;
            boostWeeks: number;
            boostEnabled: boolean;
            sprivePlus: boolean;
        };
    };
    isPrizeDrawActive: boolean;
    summary: string;
}

const REFERRAL_SEARCH_ENDPOINT = API + "/admin/referral-v2-search";
const REFERRAL_AUDIT_ENDPOINT = API + "/referral-v2-audit-log";
const REFERRAL_COMMISSION_ENDPOINT = API + "/referral-v2-commission";
const FRAUD_REVIEW_ENDPOINT = API + "/admin/referral-v2-fraud-review";

const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
    REFERRAL_CODE_USED: { label: "Referral Code Used", color: "#9c27b0" },
    USER_SIGNUP: { label: "User Signed Up", color: "#2196f3" },
    ONBOARDING_STARTED: { label: "Onboarding Started", color: "#ff9800" },
    ONBOARDING_COMPLETED: { label: "Onboarding Completed", color: "#4caf50" },
    SQS_MESSAGE_QUEUED: { label: "Processing Queued", color: "#607d8b" },
    SQS_MESSAGE_RECEIVED: { label: "Processing Started", color: "#607d8b" },
    FRAUD_CHECK_STARTED: { label: "Fraud Check Started", color: "#ff5722" },
    FRAUD_CHECK_COMPLETED: { label: "Fraud Check Completed", color: "#ff5722" },
    FRAUD_FLAG_DETECTED: { label: "Fraud Flag Detected", color: "#f44336" },
    COMMISSION_CALCULATED: { label: "Commission Calculated", color: "#3f51b5" },
    COMMISSION_CREATED: { label: "Commission Created", color: "#4caf50" },
    INFLIGHT_BALANCE_ADDED: { label: "Balance Added (Inflight)", color: "#00bcd4" },
    BOOST_ACTIVATED: { label: "Boost Activated", color: "#ff9800" },
    BOOST_EXTENDED: { label: "Boost Extended", color: "#ff9800" },
    REFERRAL_STATUS_UPDATED: { label: "Referral Status Updated", color: "#795548" },
    AMBASSADOR_PROMOTED: { label: "Promoted to Ambassador", color: "#e91e63" },
    PAYOUT_INITIATED: { label: "Payout Initiated", color: "#009688" },
    PAYOUT_COMPLETED: { label: "Payout Completed", color: "#4caf50" },
    PAYOUT_FAILED: { label: "Payout Failed", color: "#f44336" },
    ADMIN_REVIEW: { label: "Admin Review", color: "#9e9e9e" },
    ADMIN_APPROVAL: { label: "Admin Approved", color: "#4caf50" },
    ADMIN_REJECTION: { label: "Admin Rejected", color: "#f44336" },
    ERROR: { label: "Error", color: "#f44336" },
};

const ReferralUserTracking = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [legacyReferrals, setLegacyReferrals] = useState<LegacyReferral[]>([]);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [userStats, setUserStats] = useState<UserStats | null>(null);
    const [referredBy, setReferredBy] = useState<ReferredBy | null>(null);
    const [promocodeUsed, setPromocodeUsed] = useState<PromocodeUsed | null>(null);
    const [referredUserRewards, setReferredUserRewards] = useState<ReferredUserReward[]>([]);
    const [referrerRewards, setReferrerRewards] = useState<ReferredUserReward[]>([]);

    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
    const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
    const [commission, setCommission] = useState<Commission | null>(null);
    const [auditLoading, setAuditLoading] = useState(false);

    // Approval dialog state
    const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
    const [selectedReferralForApproval, setSelectedReferralForApproval] = useState<Referral | null>(null);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [approvalNote, setApprovalNote] = useState("");
    const [shoppingStatus, setShoppingStatus] = useState<any>(null);
    const [shoppingStatusLoading, setShoppingStatusLoading] = useState(false);
    const [approvalResult, setApprovalResult] = useState<any>(null);

    // Link referral dialog state (for organic signups)
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkReferrerSearch, setLinkReferrerSearch] = useState("");
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkNote, setLinkNote] = useState("");
    const [linkResult, setLinkResult] = useState<any>(null);
    const [commissionPreview, setCommissionPreview] = useState<CommissionPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Verify & process dialog state (for pending referrals)
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedReferralForVerify, setSelectedReferralForVerify] = useState<Referral | null>(null);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyNote, setVerifyNote] = useState("");
    const [verifyResult, setVerifyResult] = useState<any>(null);

    // Prize draw tickets
    const [prizeDrawTickets, setPrizeDrawTickets] = useState<PrizeDrawTicket[]>([]);
    const [hasCompletedShopping, setHasCompletedShopping] = useState(false);

    // Add referred user dialog state (when viewing a referrer)
    const [addReferredDialogOpen, setAddReferredDialogOpen] = useState(false);
    const [addReferredUserSearch, setAddReferredUserSearch] = useState("");
    const [addReferredLoading, setAddReferredLoading] = useState(false);
    const [addReferredNote, setAddReferredNote] = useState("");
    const [addReferredResult, setAddReferredResult] = useState<any>(null);
    const [addReferredValidation, setAddReferredValidation] = useState<any>(null);
    const [addReferredValidationLoading, setAddReferredValidationLoading] = useState(false);
    const [addReferredPreview, setAddReferredPreview] = useState<CommissionPreview | null>(null);

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            notify("Please enter an email or user ID", "warning");
            return;
        }

        setLoading(true);
        setReferrals([]);
        setLegacyReferrals([]);
        setUserData(null);
        setUserStats(null);
        setReferredBy(null);
        setReferredUserRewards([]);
        setReferrerRewards([]);
        setPrizeDrawTickets([]);
        setHasCompletedShopping(false);

        try {
            const config = getConfigGet({});
            const response: any = await axios.get(
                `${REFERRAL_SEARCH_ENDPOINT}?query=${encodeURIComponent(searchQuery.trim())}`,
                config
            );

            if (response.data?.status) {
                const data = response.data.data;
                setReferrals(data.referrals || []);
                setLegacyReferrals(data.legacy_referrals || []);
                setUserData(data.user || null);
                setUserStats(data.stats || null);
                setReferredBy(data.referred_by || null);
                setPromocodeUsed(data.promocode_used || null);
                setReferredUserRewards(data.referred_user_rewards || []);
                setReferrerRewards(data.referrer_rewards || []);
                setPrizeDrawTickets(data.prize_draw_tickets || []);
                setHasCompletedShopping(data.has_completed_shopping || false);

                if (data.referrals?.length === 0 && !data.referred_by && data.legacy_referrals?.length === 0) {
                    notify("No referral activity found for this user", "info");
                }
            } else {
                notify(response.data?.message || "Search failed", "warning");
            }
        } catch (error: any) {
            notify(error?.response?.data?.message || "Failed to search referrals", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleReferralClick = async (referral: Referral) => {
        setSelectedReferral(referral);
        setAuditDialogOpen(true);
        setAuditLoading(true);
        setAuditLogs([]);
        setCommission(null);

        try {
            const config = getConfigGet({});

            const auditResponse: any = await axios.get(
                `${REFERRAL_AUDIT_ENDPOINT}?referral_id=${referral.id}&$sort[createdAt]=1`,
                config
            );

            if (auditResponse.data?.data) {
                setAuditLogs(auditResponse.data.data);
            }

            const commissionResponse: any = await axios.get(
                `${REFERRAL_COMMISSION_ENDPOINT}?referral_id=${referral.id}`,
                config
            );

            if (commissionResponse.data?.data?.[0]) {
                setCommission(commissionResponse.data.data[0]);
            }
        } catch (error: any) {
            notify("Failed to load audit trail", "error");
        } finally {
            setAuditLoading(false);
        }
    };

    // Handle opening approval dialog for a rejected referral
    const handleApproveClick = async (referral: Referral, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click
        setSelectedReferralForApproval(referral);
        setApprovalDialogOpen(true);
        setApprovalNote("");
        setApprovalResult(null);
        setShoppingStatus(null);
        setShoppingStatusLoading(true);

        // Check shopping status
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "check-shopping-status",
                    referralId: referral.id
                },
                config
            );

            if (response.data?.status) {
                setShoppingStatus(response.data.data);
            }
        } catch (error: any) {
            notify("Failed to check shopping status", "error");
        } finally {
            setShoppingStatusLoading(false);
        }
    };

    // Approve only (no commission processing)
    const handleApproveOnly = async () => {
        if (!selectedReferralForApproval) return;

        setApprovalLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "approve-only",
                    referralId: selectedReferralForApproval.id,
                    notes: approvalNote || "Manual approval via user tracking"
                },
                config
            );

            if (response.data?.status) {
                setApprovalResult({
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                });
                notify("Referral approved successfully", "success");
                // Refresh search results
                handleSearch();
            } else {
                setApprovalResult({
                    success: false,
                    message: response.data?.message || "Failed to approve referral"
                });
                notify(response.data?.message || "Failed to approve", "error");
            }
        } catch (error: any) {
            setApprovalResult({
                success: false,
                message: error?.response?.data?.message || "Failed to approve referral"
            });
            notify(error?.response?.data?.message || "Failed to approve", "error");
        } finally {
            setApprovalLoading(false);
        }
    };

    // Approve and process commission
    const handleApproveAndProcess = async () => {
        if (!selectedReferralForApproval) return;

        setApprovalLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "approve-and-process",
                    referralId: selectedReferralForApproval.id,
                    notes: approvalNote || "Manual approval & process via user tracking"
                },
                config
            );

            if (response.data?.status) {
                setApprovalResult({
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                });
                notify("Referral approved and processed successfully", "success");
                // Refresh search results
                handleSearch();
            } else {
                setApprovalResult({
                    success: false,
                    message: response.data?.message || "Failed to process referral"
                });
                notify(response.data?.message || "Failed to process", "error");
            }
        } catch (error: any) {
            setApprovalResult({
                success: false,
                message: error?.response?.data?.message || "Failed to process referral"
            });
            notify(error?.response?.data?.message || "Failed to process", "error");
        } finally {
            setApprovalLoading(false);
        }
    };

    const closeApprovalDialog = () => {
        setApprovalDialogOpen(false);
        setSelectedReferralForApproval(null);
        setApprovalResult(null);
        setShoppingStatus(null);
    };

    // Open link referral dialog (for organic signups)
    const handleLinkReferralClick = () => {
        setLinkDialogOpen(true);
        setLinkReferrerSearch("");
        setLinkNote("");
        setLinkResult(null);
        setCommissionPreview(null);
    };

    // Preview commission for linking
    const handlePreviewCommission = async () => {
        if (!linkReferrerSearch.trim() || !userData) return;

        setPreviewLoading(true);
        setCommissionPreview(null);

        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "preview-commission",
                    referrerUserId: linkReferrerSearch.trim(),
                    referredUserId: userData.id
                },
                config
            );

            if (response.data?.status) {
                setCommissionPreview(response.data.data);
            } else {
                notify(response.data?.message || "Failed to preview commission", "error");
            }
        } catch (error: any) {
            notify(error?.response?.data?.message || "Failed to preview commission", "error");
        } finally {
            setPreviewLoading(false);
        }
    };

    // Link referral (create mapping + optionally process)
    const handleLinkReferral = async () => {
        if (!linkReferrerSearch.trim() || !userData) return;

        setLinkLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "link-referral",
                    referrerUserId: linkReferrerSearch.trim(),
                    referredUserId: userData.id,
                    notes: linkNote || "Linked via user tracking"
                },
                config
            );

            if (response.data?.status) {
                setLinkResult({
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                });
                notify("Referral linked successfully", "success");
                handleSearch();
            } else {
                setLinkResult({
                    success: false,
                    message: response.data?.message || "Failed to link referral"
                });
                notify(response.data?.message || "Failed to link", "error");
            }
        } catch (error: any) {
            setLinkResult({
                success: false,
                message: error?.response?.data?.message || "Failed to link referral"
            });
            notify(error?.response?.data?.message || "Failed to link", "error");
        } finally {
            setLinkLoading(false);
        }
    };

    const closeLinkDialog = () => {
        setLinkDialogOpen(false);
        setLinkReferrerSearch("");
        setLinkResult(null);
        setCommissionPreview(null);
    };

    // Open verify & process dialog for pending referrals
    const handleVerifyClick = async (referral: Referral, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedReferralForVerify(referral);
        setVerifyDialogOpen(true);
        setVerifyNote("");
        setVerifyResult(null);
        setCommissionPreview(null);
        setPreviewLoading(true);

        // Preview what would be processed
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "preview-commission",
                    referralId: referral.id
                },
                config
            );

            if (response.data?.status) {
                setCommissionPreview(response.data.data);
            }
        } catch (error: any) {
            // Silent fail for preview
        } finally {
            setPreviewLoading(false);
        }
    };

    // Verify and process commission
    const handleVerifyAndProcess = async () => {
        if (!selectedReferralForVerify) return;

        setVerifyLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "verify-and-process",
                    referralId: selectedReferralForVerify.id,
                    notes: verifyNote || "Verify & process via user tracking"
                },
                config
            );

            if (response.data?.status) {
                setVerifyResult({
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                });
                notify("Referral processed successfully", "success");
                handleSearch();
            } else {
                setVerifyResult({
                    success: false,
                    message: response.data?.message || "Failed to process referral"
                });
                notify(response.data?.message || "Failed to process", "error");
            }
        } catch (error: any) {
            setVerifyResult({
                success: false,
                message: error?.response?.data?.message || "Failed to process referral"
            });
            notify(error?.response?.data?.message || "Failed to process", "error");
        } finally {
            setVerifyLoading(false);
        }
    };

    const closeVerifyDialog = () => {
        setVerifyDialogOpen(false);
        setSelectedReferralForVerify(null);
        setVerifyResult(null);
        setCommissionPreview(null);
    };

    // Open "Add Referred User" dialog (when viewing a referrer's profile)
    const handleAddReferredClick = () => {
        setAddReferredDialogOpen(true);
        setAddReferredUserSearch("");
        setAddReferredNote("");
        setAddReferredResult(null);
        setAddReferredValidation(null);
        setAddReferredPreview(null);
    };

    // Validate the referred user before adding
    const handleValidateReferredUser = async () => {
        if (!addReferredUserSearch.trim() || !userData) return;

        setAddReferredValidationLoading(true);
        setAddReferredValidation(null);
        setAddReferredPreview(null);

        try {
            const config = getConfigGet({});

            // First validate the referral pair
            const validateResponse: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "validate-referral",
                    referrerUserId: userData.id,
                    referredUserId: addReferredUserSearch.trim(),
                    linkOnly: true // Allow validation even if user hasn't shopped
                },
                config
            );

            if (validateResponse.data?.status) {
                const validation = validateResponse.data.data;
                setAddReferredValidation(validation);

                // If valid, also get commission preview
                if (validation.valid || (validation.errors?.length === 0 && validation.warnings?.length > 0)) {
                    const previewResponse: any = await axios.post(
                        FRAUD_REVIEW_ENDPOINT,
                        {
                            action: "preview-commission",
                            referrerUserId: userData.id,
                            referredUserId: validation.referredInfo?.id || addReferredUserSearch.trim()
                        },
                        config
                    );

                    if (previewResponse.data?.status) {
                        setAddReferredPreview(previewResponse.data.data);
                    }
                }
            } else {
                setAddReferredValidation({
                    valid: false,
                    errors: [validateResponse.data?.message || "Validation failed"]
                });
            }
        } catch (error: any) {
            setAddReferredValidation({
                valid: false,
                errors: [error?.response?.data?.message || "Failed to validate user"]
            });
        } finally {
            setAddReferredValidationLoading(false);
        }
    };

    // Add the referred user (create mapping + optionally process)
    const handleAddReferredUser = async () => {
        if (!addReferredValidation?.referredInfo?.id || !userData) return;

        setAddReferredLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.post(
                FRAUD_REVIEW_ENDPOINT,
                {
                    action: "link-referral",
                    referrerUserId: userData.id,
                    referredUserId: addReferredValidation.referredInfo.id,
                    notes: addReferredNote || `Added via user tracking by ops team`
                },
                config
            );

            if (response.data?.status) {
                setAddReferredResult({
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                });
                notify("Referral added successfully", "success");
                handleSearch(); // Refresh data
            } else {
                setAddReferredResult({
                    success: false,
                    message: response.data?.message || "Failed to add referral"
                });
                notify(response.data?.message || "Failed to add referral", "error");
            }
        } catch (error: any) {
            setAddReferredResult({
                success: false,
                message: error?.response?.data?.message || "Failed to add referral"
            });
            notify(error?.response?.data?.message || "Failed to add referral", "error");
        } finally {
            setAddReferredLoading(false);
        }
    };

    const closeAddReferredDialog = () => {
        setAddReferredDialogOpen(false);
        setAddReferredUserSearch("");
        setAddReferredResult(null);
        setAddReferredValidation(null);
        setAddReferredPreview(null);
    };

    const getStatusChipClass = (status: string) => {
        switch (status) {
            case "pending": return classes.statusPending;
            case "qualified": return classes.statusQualified;
            case "completed": return classes.statusCompleted;
            case "rejected": return classes.statusRejected;
            default: return "";
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getEventInfo = (eventType: string) => {
        return EVENT_TYPE_LABELS[eventType] || { label: eventType, color: "#9e9e9e" };
    };

    return (
        <div className={classes.main}>
            <div className={classes.header}>
                <Typography className={classes.title}>Referral User Tracking</Typography>
                <Typography className={classes.subtitle}>
                    Search by email or user ID to view referral activity and audit trail
                </Typography>
            </div>

            <Paper className={classes.searchPaper}>
                <div className={classes.searchRow}>
                    <TextField
                        className={classes.searchField}
                        label="Email or User ID"
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                        placeholder="Enter email or user ID"
                    />
                    <Button
                        className={classes.searchButton}
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                    >
                        Search
                    </Button>
                </div>
            </Paper>

            {loading && (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            )}

            {!loading && userData && (
                <>
                    <div className={classes.userCard}>
                        <div className={classes.userHeader}>
                            <div className={classes.userBasicInfo}>
                                <Typography className={classes.userName}>
                                    {userData.name || "Unknown User"}
                                </Typography>
                                <Typography className={classes.userEmail}>{userData.email}</Typography>
                                <Typography className={classes.userId}>
                                    User ID: <code style={{ backgroundColor: "#f5f5f5", padding: "2px 6px", borderRadius: 3, cursor: "pointer" }}
                                                   title="Click to copy"
                                                   onClick={() => navigator.clipboard.writeText(String(userData.id))}>
                                        {userData.id}
                                    </code>
                                </Typography>
                                {userStats && (
                                    <div className={classes.badgeRow}>
                                        {userStats.is_ambassador && (
                                            <Chip
                                                size="small"
                                                label="Ambassador"
                                                className={classes.badge}
                                                style={{ backgroundColor: "#e91e63", color: "#fff" }}
                                            />
                                        )}
                                        {userStats.is_boost_active && (
                                            <Chip
                                                size="small"
                                                label={`Boost Active (${userStats.boost_weeks_remaining}w left)`}
                                                className={classes.badge}
                                                style={{ backgroundColor: "#ff9800", color: "#fff" }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                {userData.referral_code && (
                                    <div className={classes.referralCode}>
                                        <span className={classes.referralCodeLabel}>Referral Code</span>
                                        <span className={classes.referralCodeValue}>{userData.referral_code}</span>
                                    </div>
                                )}
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={handleAddReferredClick}
                                    startIcon={<PersonIcon />}
                                    style={{ textTransform: "none", borderStyle: "dashed" }}
                                >
                                    Add Referral
                                </Button>
                            </Box>
                        </div>

                        {userStats && (
                            <>
                            <Grid container spacing={2} className={classes.statsGrid}>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={classes.statBox}>
                                        <Typography className={classes.statValue}>{userStats.total_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Total</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statSuccess}`}>
                                        <Typography className={classes.statValue}>{userStats.successful_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Successful</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statPending}`}>
                                        <Typography className={classes.statValue}>{userStats.pending_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Pending</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statFlagged}`}>
                                        <Typography className={classes.statValue}>{userStats.flagged_referrals}</Typography>
                                        <Typography className={classes.statLabel}>Flagged</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statSuccess}`}>
                                        <Typography className={classes.statValue}>£{Number(userStats.paid_out || 0).toFixed(0)}</Typography>
                                        <Typography className={classes.statLabel}>Paid Out</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Box className={`${classes.statBox} ${classes.statMoney}`}>
                                        <Typography className={classes.statValue}>£{Number(userStats.inflight_amount || 0).toFixed(0)}</Typography>
                                        <Typography className={classes.statLabel}>In-flight</Typography>
                                    </Box>
                                </Grid>
                                {(userStats.legacy_referrals_count > 0) && (
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#f5f5f5" }}>
                                            <Typography className={classes.statValue} style={{ color: "#666" }}>{userStats.legacy_referrals_count}</Typography>
                                            <Typography className={classes.statLabel}>Legacy</Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                            {/* Prize Draw Tickets Row */}
                            {(userStats.total_tickets > 0) && (
                                <Grid container spacing={2} style={{ marginTop: 8 }}>
                                    <Grid item xs={12}>
                                        <Typography style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
                                            Prize Draw Tickets
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={4}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                                            <Typography className={classes.statValue} style={{ color: "#e65100" }}>{userStats.total_tickets}</Typography>
                                            <Typography className={classes.statLabel}>Total Tickets</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={4}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#e8f5e9" }}>
                                            <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>{userStats.current_week_tickets}</Typography>
                                            <Typography className={classes.statLabel}>This Week</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={4}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#fce4ec" }}>
                                            <Typography className={classes.statValue} style={{ color: "#c2185b" }}>{userStats.prize_draw_wins}</Typography>
                                            <Typography className={classes.statLabel}>Won</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                            {/* Prize Draw Stats Row */}
                            {(userStats.prize_draw_wins > 0 || userStats.prize_draw_paid_out > 0 || userStats.prize_draw_inflight > 0) && (
                                <Grid container spacing={2} style={{ marginTop: 8 }}>
                                    <Grid item xs={12}>
                                        <Typography style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>
                                            Prize Draw Earnings
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3} md={3}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                                            <Typography className={classes.statValue} style={{ color: "#e65100" }}>{userStats.prize_draw_wins}</Typography>
                                            <Typography className={classes.statLabel}>Wins</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3} md={3}>
                                        <Box className={`${classes.statBox} ${classes.statSuccess}`}>
                                            <Typography className={classes.statValue}>£{Number(userStats.prize_draw_paid_out || 0).toFixed(0)}</Typography>
                                            <Typography className={classes.statLabel}>Prize Paid</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3} md={3}>
                                        <Box className={`${classes.statBox} ${classes.statMoney}`}>
                                            <Typography className={classes.statValue}>£{Number(userStats.prize_draw_inflight || 0).toFixed(0)}</Typography>
                                            <Typography className={classes.statLabel}>Prize Pending</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3} md={3}>
                                        <Box className={classes.statBox} style={{ backgroundColor: "#fff3e0" }}>
                                            <Typography className={classes.statValue} style={{ color: "#ff9800" }}>{userStats.prize_draw_boost_weeks || 0}w</Typography>
                                            <Typography className={classes.statLabel}>Boost Won</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                            </>
                        )}
                    </div>

                    {/* How This User Joined (Referred By Section) */}
                    {referredBy ? (
                        <div className={classes.referredByCard}>
                            <Typography className={classes.referredByTitle}>
                                <PersonIcon fontSize="small" />
                                Joined Via Referral
                            </Typography>
                            <div className={classes.referredByGrid}>
                                <div className={classes.referredByItem}>
                                    <Typography className={classes.referredByLabel}>Referred By</Typography>
                                    <Typography className={classes.referredByValue}>
                                        {referredBy.referrer_name || "Unknown"}
                                    </Typography>
                                    <Typography style={{ fontSize: 11, color: "#666" }}>
                                        {referredBy.referrer_email}
                                    </Typography>
                                    <code style={{ fontSize: 11, backgroundColor: "#f5f5f5", padding: "1px 4px", borderRadius: 2, cursor: "pointer", marginTop: 4, display: "inline-block" }}
                                          title="Click to copy"
                                          onClick={() => navigator.clipboard.writeText(String(referredBy.referrer_user_id))}>
                                        ID: {referredBy.referrer_user_id}
                                    </code>
                                </div>
                                <div className={classes.referredByItem}>
                                    <Typography className={classes.referredByLabel}>Code Used</Typography>
                                    <Typography className={classes.referredByValue} style={{ fontFamily: "monospace", letterSpacing: 1 }}>
                                        {referredBy.referral_code_used || "-"}
                                    </Typography>
                                </div>
                                <div className={classes.referredByItem}>
                                    <Typography className={classes.referredByLabel}>Referral Status</Typography>
                                    <Chip
                                        size="small"
                                        label={referredBy.status}
                                        className={`${classes.statusChip} ${getStatusChipClass(referredBy.status)}`}
                                    />
                                </div>
                                <div className={classes.referredByItem}>
                                    <Typography className={classes.referredByLabel}>Joined On</Typography>
                                    <Typography className={classes.referredByValue}>
                                        {formatDate(referredBy.created_at)}
                                    </Typography>
                                </div>
                                {referredBy.completed_at && (
                                    <div className={classes.referredByItem}>
                                        <Typography className={classes.referredByLabel}>Completed On</Typography>
                                        <Typography className={classes.referredByValue}>
                                            {formatDate(referredBy.completed_at)}
                                        </Typography>
                                    </div>
                                )}
                                {referredBy.prize_draw_entry_id && (
                                    <div className={classes.referredByItem}>
                                        <Typography className={classes.referredByLabel}>Prize Draw Entry</Typography>
                                        <span className={classes.prizeDrawBadge}>
                                            <span role="img" aria-label="ticket">🎟️</span> Ticket #{referredBy.prize_draw_ticket_number || referredBy.prize_draw_entry_id}
                                        </span>
                                        <Typography style={{ fontSize: 10, color: "#666", marginTop: 4 }}>
                                            Referrer earned a prize draw ticket
                                        </Typography>
                                    </div>
                                )}
                            </div>

                            {/* Rewards this user received as referred user */}
                            {referredUserRewards && referredUserRewards.length > 0 && (
                                <div className={classes.rewardsSection}>
                                    <Typography className={classes.rewardsTitle}>
                                        This User Received (as new user)
                                    </Typography>
                                    <div className={classes.referredByGrid}>
                                        {referredUserRewards.map((reward) => (
                                            <div key={reward.id} className={classes.referredByItem} style={{ backgroundColor: "#e8f5e9" }}>
                                                <Typography className={classes.referredByLabel}>{reward.commission_type}</Typography>
                                                <Typography className={classes.referredByValue}>
                                                    £{Number(reward.amount || 0).toFixed(2)}
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 4 }}>
                                                    <Chip
                                                        size="small"
                                                        label={reward.status}
                                                        style={{
                                                            fontSize: 10,
                                                            height: 18,
                                                            backgroundColor: reward.status === "completed" ? "#c8e6c9" : "#fff3e0",
                                                            color: reward.status === "completed" ? "#2e7d32" : "#e65100",
                                                        }}
                                                    />
                                                    {reward.is_inflight && (
                                                        <Chip
                                                            size="small"
                                                            label="Inflight"
                                                            style={{ fontSize: 10, height: 18, backgroundColor: "#e3f2fd", color: "#1565c0" }}
                                                        />
                                                    )}
                                                </Box>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Rewards the referrer received for referring this user */}
                            {referrerRewards && referrerRewards.length > 0 && (
                                <div className={classes.rewardsSection}>
                                    <Typography className={classes.rewardsTitle}>
                                        Referrer Received (for referring this user)
                                    </Typography>
                                    <div className={classes.referredByGrid}>
                                        {referrerRewards.map((reward) => (
                                            <div key={reward.id} className={classes.referredByItem} style={{ backgroundColor: "#fff3e0" }}>
                                                <Typography className={classes.referredByLabel}>{reward.commission_type}</Typography>
                                                <Typography className={classes.referredByValue}>
                                                    £{Number(reward.amount || 0).toFixed(2)}
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 4 }}>
                                                    <Chip
                                                        size="small"
                                                        label={reward.status}
                                                        style={{
                                                            fontSize: 10,
                                                            height: 18,
                                                            backgroundColor: reward.status === "completed" ? "#c8e6c9" : "#fff3e0",
                                                            color: reward.status === "completed" ? "#2e7d32" : "#e65100",
                                                        }}
                                                    />
                                                    {reward.is_inflight && (
                                                        <Chip
                                                            size="small"
                                                            label="Inflight"
                                                            style={{ fontSize: 10, height: 18, backgroundColor: "#e3f2fd", color: "#1565c0" }}
                                                        />
                                                    )}
                                                    {reward.boost_weeks_earned > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={`+${reward.boost_weeks_earned}w boost`}
                                                            style={{ fontSize: 10, height: 18, backgroundColor: "#f3e5f5", color: "#7b1fa2" }}
                                                        />
                                                    )}
                                                </Box>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Paper className={classes.notReferredCard}>
                            <Typography style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                                This user did not join via referral (organic signup)
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleLinkReferralClick}
                                startIcon={<LinkIcon />}
                                style={{ textTransform: "none" }}
                            >
                                Link Referral Code
                            </Button>
                            {hasCompletedShopping && (
                                <Chip
                                    size="small"
                                    icon={<ShoppingCartIcon style={{ fontSize: 14 }} />}
                                    label="Has completed shopping"
                                    style={{ marginLeft: 12, backgroundColor: "#e8f5e9", color: "#2e7d32" }}
                                />
                            )}
                        </Paper>
                    )}

                    {/* Promocode Used Section */}
                    {promocodeUsed && (
                        <div className={classes.promocodeCard}>
                            <Typography className={classes.promocodeTitle}>
                                <span role="img" aria-label="coupon">🎫</span>
                                Promocode Used When Joining
                            </Typography>
                            <div className={classes.promocodeGrid}>
                                <div className={classes.promocodeItem}>
                                    <Typography className={classes.promocodeLabel}>Code</Typography>
                                    <Typography className={classes.promocodeCode}>
                                        {promocodeUsed.code}
                                    </Typography>
                                </div>
                                <div className={classes.promocodeItem}>
                                    <Typography className={classes.promocodeLabel}>Amount</Typography>
                                    <Typography className={classes.promocodeValue}>
                                        £{promocodeUsed.amount.toFixed(2)}
                                    </Typography>
                                </div>
                                <div className={classes.promocodeItem}>
                                    <Typography className={classes.promocodeLabel}>Status</Typography>
                                    <Chip
                                        size="small"
                                        label={promocodeUsed.status}
                                        className={`${classes.statusChip} ${getStatusChipClass(promocodeUsed.status)}`}
                                    />
                                </div>
                                {promocodeUsed.affiliate_name && (
                                    <div className={classes.promocodeItem}>
                                        <Typography className={classes.promocodeLabel}>Affiliate</Typography>
                                        <Typography className={classes.promocodeValue}>
                                            {promocodeUsed.affiliate_name}
                                        </Typography>
                                    </div>
                                )}
                                <div className={classes.promocodeItem}>
                                    <Typography className={classes.promocodeLabel}>Used On</Typography>
                                    <Typography className={classes.promocodeValue}>
                                        {formatDate(promocodeUsed.used_at)}
                                    </Typography>
                                </div>
                                <div className={classes.promocodeItem}>
                                    <Typography className={classes.promocodeLabel}>Type</Typography>
                                    <Typography className={classes.promocodeValue}>
                                        {promocodeUsed.is_sws_introducer ? "SWS Introducer" :
                                         promocodeUsed.is_remortgage_introducer ? "Remortgage" : "Standard"}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    )}

                    {referrals.length > 0 ? (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.tableHeader}>
                                <Typography className={classes.tableTitle}>
                                    Referrals Made ({referrals.length})
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Code Used</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Completed</TableCell>
                                            <TableCell align="center">Journey</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {referrals.map((referral) => (
                                            <TableRow
                                                key={referral.id}
                                                className={classes.clickableRow}
                                                onClick={() => handleReferralClick(referral)}
                                            >
                                                <TableCell>{referral.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div>{referral.referred_name || "Unknown"}</div>
                                                            <code style={{ fontSize: 11, backgroundColor: "#f5f5f5", padding: "1px 4px", borderRadius: 2, cursor: "pointer" }}
                                                                  title="Click to copy"
                                                                  onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(String(referral.referred_user_id)); }}>
                                                                ID: {referral.referred_user_id}
                                                            </code>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{referral.referred_email || "-"}</TableCell>
                                                <TableCell>
                                                    <code style={{ fontSize: 12, color: "#666" }}>
                                                        {referral.referral_code || "-"}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={referral.status}
                                                        className={`${classes.statusChip} ${getStatusChipClass(referral.status)}`}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(referral.createdAt)}</TableCell>
                                                <TableCell>{formatDate(referral.completed_at)}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="primary">
                                                        <TimelineIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {referral.status === "rejected" && (
                                                        <Tooltip title="Approve this rejected referral">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={(e) => handleApproveClick(referral, e)}
                                                                startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                                                                style={{ fontSize: 11, textTransform: "none" }}
                                                            >
                                                                Approve
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {(referral.status === "pending" || referral.status === "qualified") && (
                                                        <Tooltip title="Verify and process commission (if user has shopped)">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                style={{ fontSize: 11, textTransform: "none", borderColor: "#ff9800", color: "#ff9800" }}
                                                                onClick={(e) => handleVerifyClick(referral, e)}
                                                                startIcon={<RefreshIcon fontSize="small" />}
                                                            >
                                                                Verify & Process
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : (
                        <Paper className={classes.tablePaper}>
                            <div className={classes.noData}>
                                <Typography>No referrals found for this user</Typography>
                            </div>
                        </Paper>
                    )}

                    {/* Legacy Referrals (Old System) */}
                    {legacyReferrals.length > 0 && (
                        <Paper className={classes.tablePaper} style={{ marginTop: 16 }}>
                            <div className={classes.tableHeader} style={{ backgroundColor: "#f5f5f5" }}>
                                <Typography className={classes.tableTitle} style={{ color: "#666" }}>
                                    Legacy Referrals ({legacyReferrals.length}) - Old System
                                </Typography>
                                <Typography style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                    Referrals from before the new referral system was implemented
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Joined On</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {legacyReferrals.map((ref) => (
                                            <TableRow key={ref.id}>
                                                <TableCell>{ref.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                        <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                        <div>
                                                            <div>{ref.referred_name || "Unknown"}</div>
                                                            <code style={{ fontSize: 11, backgroundColor: "#f5f5f5", padding: "1px 4px", borderRadius: 2, cursor: "pointer" }}
                                                                  title="Click to copy"
                                                                  onClick={() => navigator.clipboard.writeText(String(ref.referred_user_id))}>
                                                                ID: {ref.referred_user_id}
                                                            </code>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{ref.referred_email || "-"}</TableCell>
                                                <TableCell>{formatDate(ref.created_at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}

                    {/* Prize Draw Tickets */}
                    {prizeDrawTickets.length > 0 && (
                        <Paper className={classes.tablePaper} style={{ marginTop: 16 }}>
                            <div className={classes.tableHeader} style={{ backgroundColor: "#fff3e0" }}>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    <ConfirmationNumberIcon style={{ color: "#ff9800" }} />
                                    <Typography className={classes.tableTitle} style={{ color: "#e65100" }}>
                                        Prize Draw Tickets ({prizeDrawTickets.length})
                                    </Typography>
                                </Box>
                                <Typography style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                    Tickets earned from successful referrals during prize draw campaigns
                                </Typography>
                            </div>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ticket #</TableCell>
                                            <TableCell>Campaign</TableCell>
                                            <TableCell>Week</TableCell>
                                            <TableCell>Multiplier</TableCell>
                                            <TableCell>Effective Tickets</TableCell>
                                            <TableCell>Referred User</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Entry Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {prizeDrawTickets.map((ticket) => (
                                            <TableRow key={ticket.id}>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={`🎟️ #${ticket.ticket_number}`}
                                                        style={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>{ticket.campaign_name || "-"}</TableCell>
                                                <TableCell>
                                                    <Typography style={{ fontSize: 12 }}>
                                                        {formatDate(ticket.week_start).split(",")[0]} - {formatDate(ticket.week_end).split(",")[0]}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={ticket.week_status}
                                                        style={{
                                                            fontSize: 10,
                                                            height: 18,
                                                            backgroundColor: ticket.week_status === "active" ? "#e8f5e9" : "#f5f5f5",
                                                            color: ticket.week_status === "active" ? "#2e7d32" : "#666"
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={`${ticket.multiplier_applied}x`}
                                                        style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ fontWeight: 600, color: "#ff9800" }}>
                                                    {ticket.effective_tickets}
                                                </TableCell>
                                                <TableCell>
                                                    <div>{ticket.referred_name || "Unknown"}</div>
                                                    <Typography style={{ fontSize: 11, color: "#666" }}>
                                                        {ticket.referred_email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={ticket.entry_status}
                                                        style={{
                                                            fontSize: 10,
                                                            backgroundColor: ticket.entry_status === "active" ? "#e8f5e9" : "#f5f5f5",
                                                            color: ticket.entry_status === "active" ? "#2e7d32" : "#666"
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(ticket.entry_timestamp)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </>
            )}

            <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Referral Journey #{selectedReferral?.id}
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                {selectedReferral?.referred_email || `User #${selectedReferral?.referred_user_id}`}
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={() => setAuditDialogOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {auditLoading ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            {commission && (
                                <div className={classes.commissionCard}>
                                    <Typography className={classes.commissionTitle}>Commission Details</Typography>
                                    <div className={classes.commissionGrid}>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Amount</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                £{Number(commission.amount || 0).toFixed(2)}
                                            </Typography>
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Type</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                {commission.commission_type}
                                            </Typography>
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Status</Typography>
                                            <Chip
                                                size="small"
                                                label={commission.status}
                                                style={{
                                                    backgroundColor: commission.status === "completed" ? "#e8f5e9" : "#fff3e0",
                                                    color: commission.status === "completed" ? "#2e7d32" : "#e65100",
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Fraud</Typography>
                                            <Chip
                                                size="small"
                                                label={commission.fraud_status}
                                                style={{
                                                    backgroundColor: commission.fraud_status === "clean" ? "#e8f5e9" : "#ffebee",
                                                    color: commission.fraud_status === "clean" ? "#2e7d32" : "#c62828",
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        </div>
                                        <div className={classes.commissionItem}>
                                            <Typography className={classes.commissionItemLabel}>Inflight</Typography>
                                            <Typography className={classes.commissionItemValue}>
                                                {commission.is_inflight ? "Yes" : "No"}
                                            </Typography>
                                        </div>
                                        {commission.config_id && (
                                            <div className={classes.commissionItem}>
                                                <Typography className={classes.commissionItemLabel}>Campaign</Typography>
                                                <Typography className={classes.commissionItemValue}>
                                                    #{commission.config_id}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {auditLogs.length > 0 ? (
                                <Stepper orientation="vertical" style={{ padding: 0 }}>
                                    {auditLogs.map((log) => {
                                        const eventInfo = getEventInfo(log.event_type);
                                        return (
                                            <Step key={log.id} active expanded className={classes.auditStep}>
                                                <StepLabel
                                                    StepIconComponent={() => (
                                                        <div
                                                            style={{
                                                                width: 22,
                                                                height: 22,
                                                                borderRadius: "50%",
                                                                backgroundColor: eventInfo.color,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {log.event_type.includes("ERROR") ? (
                                                                <ErrorIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            ) : log.event_type.includes("COMPLETED") || log.event_type.includes("CREATED") ? (
                                                                <CheckCircleIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            ) : (
                                                                <HourglassEmptyIcon style={{ color: "#fff", fontSize: 14 }} />
                                                            )}
                                                        </div>
                                                    )}
                                                >
                                                    <span style={{ color: eventInfo.color, fontWeight: 600, fontSize: 13 }}>
                                                        {eventInfo.label}
                                                    </span>
                                                </StepLabel>
                                                <StepContent className={classes.auditStepContent}>
                                                    <div className={classes.auditTime}>
                                                        {formatDate(log.createdAt)}
                                                        {log.source && <span> · {log.source}</span>}
                                                    </div>
                                                    {log.previous_state && log.new_state && (
                                                        <Typography style={{ fontSize: 12, marginBottom: 4 }}>
                                                            {log.previous_state} → {log.new_state}
                                                        </Typography>
                                                    )}
                                                    {log.error_message && (
                                                        <Typography style={{ fontSize: 12, color: "#c62828", marginBottom: 4 }}>
                                                            {log.error_message}
                                                        </Typography>
                                                    )}
                                                    {log.event_data && Object.keys(log.event_data).length > 0 && (
                                                        <div className={classes.auditData}>
                                                            <pre style={{ margin: 0 }}>
                                                                {JSON.stringify(log.event_data, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            ) : (
                                <div className={classes.noData}>
                                    <Typography>No audit trail found</Typography>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Link Referral Dialog */}
            <Dialog open={linkDialogOpen} onClose={closeLinkDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Link Referral Code
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                Link {userData?.email} to a referrer
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={closeLinkDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    {linkResult ? (
                        <Box textAlign="center" py={2}>
                            {linkResult.success ? (
                                <>
                                    <CheckCircleIcon style={{ fontSize: 48, color: "#4caf50", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                        {linkResult.message}
                                    </Typography>
                                    {linkResult.data?.processed && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Commission processed: £{Number(linkResult.data?.commissionAmount || 0).toFixed(2)}
                                        </Typography>
                                    )}
                                    {!linkResult.data?.processed && (
                                        <Typography style={{ fontSize: 13, color: "#ff9800", marginTop: 8 }}>
                                            Commission will be processed when user completes first gift card purchase.
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <ErrorIcon style={{ fontSize: 48, color: "#f44336", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#c62828" }}>
                                        {linkResult.message}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Box mb={3}>
                                <Typography style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                                    Enter the referrer's email, user ID, or referral code to link this user to them.
                                </Typography>
                                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Referrer Email, User ID, or Referral Code"
                                        variant="outlined"
                                        size="small"
                                        value={linkReferrerSearch}
                                        onChange={(e) => setLinkReferrerSearch(e.target.value)}
                                        placeholder="e.g. john@example.com, 12345, or ABCD1234"
                                    />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handlePreviewCommission}
                                        disabled={!linkReferrerSearch.trim() || previewLoading}
                                        style={{ minWidth: 120, textTransform: "none" }}
                                    >
                                        {previewLoading ? <CircularProgress size={20} /> : "Preview"}
                                    </Button>
                                </Box>
                            </Box>

                            {/* Commission Preview */}
                            {commissionPreview && (
                                <Box mb={3} p={2} style={{ backgroundColor: "#f8f9fa", borderRadius: 8, border: "1px solid #e0e0e0" }}>
                                    <Typography style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: "#1a1a1a" }}>
                                        Commission Preview - {commissionPreview.campaignName}
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {/* Referrer Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e8f5e9", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                                    REFERRER GETS
                                                </Typography>
                                                <Typography style={{ fontSize: 13, marginBottom: 4 }}>
                                                    {commissionPreview.referrer.name} ({commissionPreview.referrer.email})
                                                </Typography>
                                                {commissionPreview.referrer.rewards.isPrizeDrawMode ? (
                                                    <Box mt={1}>
                                                        <Chip
                                                            icon={<ConfirmationNumberIcon style={{ fontSize: 14 }} />}
                                                            label={`Prize Draw Ticket (${commissionPreview.referrer.rewards.prizeDrawTicket?.multiplier || 1}x multiplier)`}
                                                            style={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: 600 }}
                                                        />
                                                        <Typography style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                                                            {commissionPreview.referrer.rewards.prizeDrawTicket?.effectiveTickets || 1} effective ticket(s)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box mt={1}>
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#2e7d32" }}>
                                                            £{commissionPreview.referrer.rewards.commission?.amount || 0}
                                                        </Typography>
                                                        <Typography style={{ fontSize: 11, color: "#666" }}>
                                                            {commissionPreview.referrer.rewards.commission?.commissionType || "Commission"}
                                                        </Typography>
                                                        {(commissionPreview.referrer.rewards.commission?.boostWeeks || 0) > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`+${commissionPreview.referrer.rewards.commission?.boostWeeks} boost weeks`}
                                                                style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                            />
                                                        )}
                                                        {commissionPreview.referrer.rewards.commission?.sprivePlus && (
                                                            <Chip
                                                                size="small"
                                                                label="Sprive Plus"
                                                                style={{ marginTop: 4, marginLeft: 4, backgroundColor: "#e3f2fd", color: "#1565c0", fontSize: 10 }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                {commissionPreview.referrer.rewards.wouldBecomeAmbassador && (
                                                    <Chip
                                                        size="small"
                                                        label="Would become Ambassador!"
                                                        style={{ marginTop: 8, backgroundColor: "#e91e63", color: "#fff", fontSize: 10 }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>

                                        {/* Referred User Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#1565c0", marginBottom: 8 }}>
                                                    REFERRED USER GETS
                                                </Typography>
                                                <Typography style={{ fontSize: 13, marginBottom: 4 }}>
                                                    {userData?.name} ({userData?.email})
                                                </Typography>
                                                <Box mt={1}>
                                                    {commissionPreview.referred.rewards.bonusAmount > 0 && (
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#1565c0" }}>
                                                            £{commissionPreview.referred.rewards.bonusAmount}
                                                        </Typography>
                                                    )}
                                                    {commissionPreview.referred.rewards.boostWeeks > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={`+${commissionPreview.referred.rewards.boostWeeks} boost weeks`}
                                                            style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                        />
                                                    )}
                                                    {commissionPreview.referred.rewards.sprivePlus && (
                                                        <Chip
                                                            size="small"
                                                            label="Sprive Plus"
                                                            style={{ marginTop: 4, marginLeft: 4, backgroundColor: "#e3f2fd", color: "#1565c0", fontSize: 10 }}
                                                        />
                                                    )}
                                                    {!commissionPreview.referred.rewards.bonusAmount && !commissionPreview.referred.rewards.boostWeeks && !commissionPreview.referred.rewards.sprivePlus && (
                                                        <Typography style={{ fontSize: 13, color: "#666" }}>No direct rewards</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box mt={2} p={1} style={{ backgroundColor: commissionPreview.isPrizeDrawActive ? "#fff3e0" : "#f5f5f5", borderRadius: 4 }}>
                                        <Typography style={{ fontSize: 12, color: commissionPreview.isPrizeDrawActive ? "#e65100" : "#666" }}>
                                            {commissionPreview.isPrizeDrawActive ? "🎰 Prize Draw is ACTIVE" : "💰 Regular Commission Mode"}
                                            {" - "}{commissionPreview.summary}
                                        </Typography>
                                    </Box>

                                    {hasCompletedShopping && (
                                        <Box mt={2} p={1} style={{ backgroundColor: "#e8f5e9", borderRadius: 4 }}>
                                            <Typography style={{ fontSize: 12, color: "#2e7d32" }}>
                                                ✓ User has completed shopping - commission will be processed immediately
                                            </Typography>
                                        </Box>
                                    )}
                                    {!hasCompletedShopping && (
                                        <Box mt={2} p={1} style={{ backgroundColor: "#fff3e0", borderRadius: 4 }}>
                                            <Typography style={{ fontSize: 12, color: "#e65100" }}>
                                                <span role="img" aria-label="hourglass">⏳</span> User has NOT completed shopping - referral will be linked, commission processes when user shops
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                label="Notes (optional)"
                                variant="outlined"
                                size="small"
                                multiline
                                rows={2}
                                value={linkNote}
                                onChange={(e) => setLinkNote(e.target.value)}
                                placeholder="Add any notes about this referral link..."
                            />
                        </>
                    )}
                </DialogContent>
                {!linkResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button onClick={closeLinkDialog} disabled={linkLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleLinkReferral}
                            disabled={linkLoading || !commissionPreview}
                            startIcon={linkLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                        >
                            {linkLoading ? "Linking..." : hasCompletedShopping ? "Link & Process" : "Link Referral"}
                        </Button>
                    </DialogActions>
                )}
                {linkResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button variant="contained" color="primary" onClick={closeLinkDialog}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Verify & Process Dialog */}
            <Dialog open={verifyDialogOpen} onClose={closeVerifyDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Verify & Process Referral #{selectedReferralForVerify?.id}
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                {selectedReferralForVerify?.referred_email || `User #${selectedReferralForVerify?.referred_user_id}`}
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={closeVerifyDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    {verifyResult ? (
                        <Box textAlign="center" py={2}>
                            {verifyResult.success ? (
                                <>
                                    <CheckCircleIcon style={{ fontSize: 48, color: "#4caf50", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                        {verifyResult.message}
                                    </Typography>
                                    {verifyResult.data?.commissionAmount && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Commission: £{Number(verifyResult.data.commissionAmount).toFixed(2)}
                                        </Typography>
                                    )}
                                    {verifyResult.data?.boostWeeksAdded > 0 && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Boost: {verifyResult.data.boostWeeksAdded} weeks added
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <ErrorIcon style={{ fontSize: 48, color: "#f44336", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#c62828" }}>
                                        {verifyResult.message}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Box mb={3} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                                <Typography style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>
                                    This will verify that the referred user has completed shopping and process the commission if it hasn't been processed yet.
                                    Use this for edge cases where the referral mapping exists but commission wasn't automatically processed.
                                </Typography>
                            </Box>

                            {/* Commission Preview */}
                            {previewLoading ? (
                                <Box textAlign="center" py={3}>
                                    <CircularProgress size={24} />
                                    <Typography style={{ fontSize: 13, color: "#666", marginTop: 8 }}>Loading preview...</Typography>
                                </Box>
                            ) : commissionPreview && (
                                <Box mb={3} p={2} style={{ backgroundColor: "#f8f9fa", borderRadius: 8, border: "1px solid #e0e0e0" }}>
                                    <Typography style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: "#1a1a1a" }}>
                                        Commission Preview - {commissionPreview.campaignName}
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {/* Referrer Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e8f5e9", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                                    REFERRER GETS
                                                </Typography>
                                                <Typography style={{ fontSize: 13, marginBottom: 4 }}>
                                                    {commissionPreview.referrer.name} ({commissionPreview.referrer.email})
                                                </Typography>
                                                {commissionPreview.referrer.rewards.isPrizeDrawMode ? (
                                                    <Box mt={1}>
                                                        <Chip
                                                            icon={<ConfirmationNumberIcon style={{ fontSize: 14 }} />}
                                                            label={`Prize Draw Ticket (${commissionPreview.referrer.rewards.prizeDrawTicket?.multiplier || 1}x)`}
                                                            style={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: 600 }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Box mt={1}>
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#2e7d32" }}>
                                                            £{commissionPreview.referrer.rewards.commission?.amount || 0}
                                                        </Typography>
                                                        {(commissionPreview.referrer.rewards.commission?.boostWeeks || 0) > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`+${commissionPreview.referrer.rewards.commission?.boostWeeks}w boost`}
                                                                style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>

                                        {/* Referred User Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#1565c0", marginBottom: 8 }}>
                                                    REFERRED USER GETS
                                                </Typography>
                                                <Typography style={{ fontSize: 13, marginBottom: 4 }}>
                                                    {commissionPreview.referred.name} ({commissionPreview.referred.email})
                                                </Typography>
                                                <Box mt={1}>
                                                    {commissionPreview.referred.rewards.bonusAmount > 0 && (
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#1565c0" }}>
                                                            £{commissionPreview.referred.rewards.bonusAmount}
                                                        </Typography>
                                                    )}
                                                    {commissionPreview.referred.rewards.boostWeeks > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={`+${commissionPreview.referred.rewards.boostWeeks}w boost`}
                                                            style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box mt={2} p={1} style={{ backgroundColor: commissionPreview.isPrizeDrawActive ? "#fff3e0" : "#f5f5f5", borderRadius: 4 }}>
                                        <Typography style={{ fontSize: 12, color: commissionPreview.isPrizeDrawActive ? "#e65100" : "#666" }}>
                                            {commissionPreview.summary}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                label="Notes (optional)"
                                variant="outlined"
                                size="small"
                                multiline
                                rows={2}
                                value={verifyNote}
                                onChange={(e) => setVerifyNote(e.target.value)}
                                placeholder="Add any notes about this verification..."
                            />
                        </>
                    )}
                </DialogContent>
                {!verifyResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button onClick={closeVerifyDialog} disabled={verifyLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#ff9800", color: "#fff" }}
                            onClick={handleVerifyAndProcess}
                            disabled={verifyLoading || previewLoading}
                            startIcon={verifyLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        >
                            {verifyLoading ? "Processing..." : "Verify & Process"}
                        </Button>
                    </DialogActions>
                )}
                {verifyResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button variant="contained" color="primary" onClick={closeVerifyDialog}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Approval Dialog */}
            <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Approve Referral #{selectedReferralForApproval?.id}
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                {selectedReferralForApproval?.referred_email || `User #${selectedReferralForApproval?.referred_user_id}`}
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={closeApprovalDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    {approvalResult ? (
                        // Show result
                        <Box textAlign="center" py={2}>
                            {approvalResult.success ? (
                                <>
                                    <CheckCircleIcon style={{ fontSize: 48, color: "#4caf50", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                        {approvalResult.message}
                                    </Typography>
                                    {approvalResult.data?.commissionAmount && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Commission: £{Number(approvalResult.data.commissionAmount).toFixed(2)}
                                        </Typography>
                                    )}
                                    {approvalResult.data?.boostWeeksAdded > 0 && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Boost: {approvalResult.data.boostWeeksAdded} weeks added
                                        </Typography>
                                    )}
                                    {approvalResult.data?.newStatus === "pending" && (
                                        <Typography style={{ fontSize: 13, color: "#ff9800", marginTop: 12 }}>
                                            Commission will be processed when user completes their first gift card purchase.
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <ErrorIcon style={{ fontSize: 48, color: "#f44336", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#c62828" }}>
                                        {approvalResult.message}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <>
                            {/* Shopping Status Check */}
                            <Box
                                p={2}
                                mb={2}
                                style={{
                                    backgroundColor: shoppingStatus?.hasCompletedPurchase ? "#e8f5e9" : "#fff3e0",
                                    borderRadius: 8,
                                    border: `1px solid ${shoppingStatus?.hasCompletedPurchase ? "#c8e6c9" : "#ffcc80"}`
                                }}
                            >
                                <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                                    <ShoppingCartIcon style={{ color: shoppingStatus?.hasCompletedPurchase ? "#4caf50" : "#ff9800" }} />
                                    <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                                        Shopping Status
                                    </Typography>
                                </Box>
                                {shoppingStatusLoading ? (
                                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                        <CircularProgress size={16} />
                                        <Typography style={{ fontSize: 13, color: "#666" }}>Checking...</Typography>
                                    </Box>
                                ) : shoppingStatus ? (
                                    <>
                                        <Typography style={{ fontSize: 13, color: shoppingStatus.hasCompletedPurchase ? "#2e7d32" : "#e65100" }}>
                                            {shoppingStatus.hasCompletedPurchase
                                                ? `✓ User has completed ${shoppingStatus.purchaseCount} gift card purchase(s)`
                                                : "✗ User has NOT completed any gift card purchases yet"
                                            }
                                        </Typography>
                                        {shoppingStatus.firstPurchase && (
                                            <Typography style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                                                First purchase: {shoppingStatus.firstPurchase.brand_name} - £{shoppingStatus.firstPurchase.amount} on {formatDate(shoppingStatus.firstPurchase.createdAt)}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <Typography style={{ fontSize: 13, color: "#666" }}>Could not check shopping status</Typography>
                                )}
                            </Box>

                            {/* Explanation */}
                            <Box mb={2} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                                <Typography style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>
                                    {shoppingStatus?.hasCompletedPurchase ? (
                                        <>
                                            <strong>Approve & Process:</strong> This will approve the referral AND immediately process the commission (add to referrer's inflight balance, activate boost, etc.).
                                        </>
                                    ) : (
                                        <>
                                            <strong>Approve Only:</strong> This will approve the referral and change its status to "pending". The commission will be automatically processed when the referred user completes their first gift card purchase.
                                        </>
                                    )}
                                </Typography>
                            </Box>

                            {/* Notes */}
                            <TextField
                                fullWidth
                                label="Notes (optional)"
                                variant="outlined"
                                size="small"
                                multiline
                                rows={2}
                                value={approvalNote}
                                onChange={(e) => setApprovalNote(e.target.value)}
                                placeholder="Add any notes about this approval..."
                                style={{ marginBottom: 16 }}
                            />
                        </>
                    )}
                </DialogContent>
                {!approvalResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button onClick={closeApprovalDialog} disabled={approvalLoading}>
                            Cancel
                        </Button>
                        {shoppingStatus?.hasCompletedPurchase ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleApproveAndProcess}
                                disabled={approvalLoading || shoppingStatusLoading}
                                startIcon={approvalLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                            >
                                {approvalLoading ? "Processing..." : "Approve & Process"}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "#ff9800", color: "#fff" }}
                                onClick={handleApproveOnly}
                                disabled={approvalLoading || shoppingStatusLoading}
                                startIcon={approvalLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                            >
                                {approvalLoading ? "Approving..." : "Approve Only"}
                            </Button>
                        )}
                    </DialogActions>
                )}
                {approvalResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button variant="contained" color="primary" onClick={closeApprovalDialog}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Add Referred User Dialog */}
            <Dialog open={addReferredDialogOpen} onClose={closeAddReferredDialog} maxWidth="md" fullWidth>
                <DialogTitle style={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                            <Typography style={{ fontSize: 16, fontWeight: 600 }}>
                                Add Referred User
                            </Typography>
                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                Add a user as referred by {userData?.name} ({userData?.email})
                            </Typography>
                        </div>
                        <IconButton size="small" onClick={closeAddReferredDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ padding: 20 }}>
                    {addReferredResult ? (
                        <Box textAlign="center" py={2}>
                            {addReferredResult.success ? (
                                <>
                                    <CheckCircleIcon style={{ fontSize: 48, color: "#4caf50", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                        {addReferredResult.message}
                                    </Typography>
                                    {addReferredResult.data?.processed && (
                                        <Typography style={{ fontSize: 14, color: "#666" }}>
                                            Commission processed: £{Number(addReferredResult.data?.commissionAmount || 0).toFixed(2)}
                                        </Typography>
                                    )}
                                    {!addReferredResult.data?.processed && (
                                        <Typography style={{ fontSize: 13, color: "#ff9800", marginTop: 8 }}>
                                            Commission will be processed when user completes first gift card purchase.
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <ErrorIcon style={{ fontSize: 48, color: "#f44336", marginBottom: 16 }} />
                                    <Typography style={{ fontSize: 16, fontWeight: 600, color: "#c62828" }}>
                                        {addReferredResult.message}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Box mb={3}>
                                <Typography style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                                    Enter the email or user ID of the user who should be linked as a referral.
                                </Typography>
                                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Referred User Email or ID"
                                        variant="outlined"
                                        size="small"
                                        value={addReferredUserSearch}
                                        onChange={(e) => setAddReferredUserSearch(e.target.value)}
                                        placeholder="Enter email or user ID of the referred user"
                                    />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleValidateReferredUser}
                                        disabled={!addReferredUserSearch.trim() || addReferredValidationLoading}
                                        style={{ minWidth: 120, textTransform: "none" }}
                                    >
                                        {addReferredValidationLoading ? <CircularProgress size={20} /> : "Validate"}
                                    </Button>
                                </Box>
                            </Box>

                            {/* Validation Results */}
                            {addReferredValidation && (
                                <Box mb={3} p={2} style={{
                                    backgroundColor: addReferredValidation.valid ? "#e8f5e9" : (addReferredValidation.warnings?.length > 0 ? "#fff3e0" : "#ffebee"),
                                    borderRadius: 8,
                                    border: `1px solid ${addReferredValidation.valid ? "#c8e6c9" : (addReferredValidation.warnings?.length > 0 ? "#ffcc80" : "#ef9a9a")}`
                                }}>
                                    {addReferredValidation.referredInfo && (
                                        <Box mb={2} pb={2} style={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                                            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4 }}>
                                                REFERRED USER
                                            </Typography>
                                            <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                                                {addReferredValidation.referredInfo.first_name} {addReferredValidation.referredInfo.last_name}
                                            </Typography>
                                            <Typography style={{ fontSize: 13, color: "#666" }}>
                                                {addReferredValidation.referredInfo.email} (ID: {addReferredValidation.referredInfo.id})
                                            </Typography>
                                        </Box>
                                    )}

                                    {addReferredValidation.errors?.length > 0 && (
                                        <Box>
                                            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#c62828", marginBottom: 4 }}>
                                                ✗ VALIDATION ERRORS
                                            </Typography>
                                            {addReferredValidation.errors.map((err: string, i: number) => (
                                                <Typography key={i} style={{ fontSize: 13, color: "#c62828" }}>
                                                    • {err}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}

                                    {addReferredValidation.warnings?.length > 0 && (
                                        <Box mt={addReferredValidation.errors?.length > 0 ? 2 : 0}>
                                            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#e65100", marginBottom: 4 }}>
                                                ⚠ WARNINGS
                                            </Typography>
                                            {addReferredValidation.warnings.map((warn: string, i: number) => (
                                                <Typography key={i} style={{ fontSize: 13, color: "#e65100" }}>
                                                    • {warn}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}

                                    {addReferredValidation.valid && (
                                        <Box>
                                            <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 4 }}>
                                                ✓ VALIDATION PASSED
                                            </Typography>
                                            <Typography style={{ fontSize: 13, color: "#2e7d32" }}>
                                                {addReferredValidation.hasCompletedPurchase
                                                    ? "User has completed shopping - commission will be processed immediately."
                                                    : "User has NOT completed shopping yet - referral will be linked, commission processes when they shop."
                                                }
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Commission Preview */}
                            {addReferredPreview && (
                                <Box mb={3} p={2} style={{ backgroundColor: "#f8f9fa", borderRadius: 8, border: "1px solid #e0e0e0" }}>
                                    <Typography style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: "#1a1a1a" }}>
                                        Commission Preview - {addReferredPreview.campaignName}
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {/* Referrer Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e8f5e9", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32", marginBottom: 8 }}>
                                                    REFERRER GETS ({userData?.name})
                                                </Typography>
                                                {addReferredPreview.referrer.rewards.isPrizeDrawMode ? (
                                                    <Box>
                                                        <Chip
                                                            icon={<ConfirmationNumberIcon style={{ fontSize: 14 }} />}
                                                            label={`Prize Draw Ticket (${addReferredPreview.referrer.rewards.prizeDrawTicket?.multiplier || 1}x)`}
                                                            style={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: 600 }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#2e7d32" }}>
                                                            £{addReferredPreview.referrer.rewards.commission?.amount || 0}
                                                        </Typography>
                                                        {(addReferredPreview.referrer.rewards.commission?.boostWeeks || 0) > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`+${addReferredPreview.referrer.rewards.commission?.boostWeeks}w boost`}
                                                                style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                {addReferredPreview.referrer.rewards.wouldBecomeAmbassador && (
                                                    <Chip
                                                        size="small"
                                                        label="Would become Ambassador!"
                                                        style={{ marginTop: 8, backgroundColor: "#e91e63", color: "#fff", fontSize: 10 }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>

                                        {/* Referred User Rewards */}
                                        <Grid item xs={12} md={6}>
                                            <Box p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 6 }}>
                                                <Typography style={{ fontSize: 12, fontWeight: 600, color: "#1565c0", marginBottom: 8 }}>
                                                    REFERRED USER GETS
                                                </Typography>
                                                <Box>
                                                    {addReferredPreview.referred.rewards.bonusAmount > 0 && (
                                                        <Typography style={{ fontSize: 18, fontWeight: 700, color: "#1565c0" }}>
                                                            £{addReferredPreview.referred.rewards.bonusAmount}
                                                        </Typography>
                                                    )}
                                                    {addReferredPreview.referred.rewards.boostWeeks > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={`+${addReferredPreview.referred.rewards.boostWeeks}w boost`}
                                                            style={{ marginTop: 4, backgroundColor: "#fff3e0", color: "#e65100", fontSize: 10 }}
                                                        />
                                                    )}
                                                    {addReferredPreview.referred.rewards.sprivePlus && (
                                                        <Chip
                                                            size="small"
                                                            label="Sprive Plus"
                                                            style={{ marginTop: 4, marginLeft: 4, backgroundColor: "#e3f2fd", color: "#1565c0", fontSize: 10 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box mt={2} p={1} style={{ backgroundColor: addReferredPreview.isPrizeDrawActive ? "#fff3e0" : "#f5f5f5", borderRadius: 4 }}>
                                        <Typography style={{ fontSize: 12, color: addReferredPreview.isPrizeDrawActive ? "#e65100" : "#666" }}>
                                            {addReferredPreview.isPrizeDrawActive ? "🎰 Prize Draw is ACTIVE" : "💰 Regular Commission Mode"}
                                            {" - "}{addReferredPreview.summary}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                label="Notes (optional)"
                                variant="outlined"
                                size="small"
                                multiline
                                rows={2}
                                value={addReferredNote}
                                onChange={(e) => setAddReferredNote(e.target.value)}
                                placeholder="Add any notes about this referral..."
                            />
                        </>
                    )}
                </DialogContent>
                {!addReferredResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button onClick={closeAddReferredDialog} disabled={addReferredLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddReferredUser}
                            disabled={addReferredLoading || (!addReferredValidation?.valid && !(addReferredValidation?.errors?.length === 0))}
                            startIcon={addReferredLoading ? <CircularProgress size={16} color="inherit" /> : <PersonIcon />}
                        >
                            {addReferredLoading ? "Adding..." : addReferredValidation?.hasCompletedPurchase ? "Add & Process" : "Add Referral"}
                        </Button>
                    </DialogActions>
                )}
                {addReferredResult && (
                    <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                        <Button variant="contained" color="primary" onClick={closeAddReferredDialog}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <Notification />
        </div>
    );
};

export default ReferralUserTracking;
